import { useContext, useEffect, useRef } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import ZillowMapContext from '@zillow/zillow-map-control/dist/ZillowMapContext';
import { formatCurrency } from '../../../../utils/formatCurrency';

type PriceMapDotProps = {
  lat: number;
  lng: number;
  price: number;
  backgroundColor?: string;
  textColor?: string;
  zIndex?: number;
  popoverTitle?: string;
  popoverContent?: string | React.ReactNode;
  popoverHeaderAction?: React.ReactNode;
  onOpen?: () => void;
  showDollarSign?: boolean;
};

const createPriceLabel = (price: number, backgroundColor: string, textColor: string, showDollarSign = true): string => {
  const formattedPrice = showDollarSign ? formatCurrency(price, { compact: true }) : String(price);

  // Create a pin/marker shape with price inside
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 60 80">
      <!-- Pin shadow -->
      <ellipse cx="30" cy="75" rx="8" ry="3" fill="rgba(0,0,0,0.3)"/>

      <!-- Pin body - teardrop shape -->
      <path d="M 30 5
               C 15 5, 5 15, 5 30
               C 5 40, 10 50, 30 75
               C 50 50, 55 40, 55 30
               C 55 15, 45 5, 30 5 Z"
            fill="${backgroundColor}"
            stroke="#111116"
            stroke-width="2"/>

      <!-- Price text -->
      <text x="30" y="32"
            text-anchor="middle"
            dominant-baseline="middle"
            font-family="-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
            font-size="17"
            font-weight="700"
            fill="${textColor}">${formattedPrice}</text>
    </svg>
  `;

  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
};

const createPopoverHeaderElement = (title: string, headerAction?: React.ReactNode): HTMLDivElement => {
  const container = document.createElement('div');
  container.style.cssText = `
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    min-width: 240px;
  `;

  const titleEl = document.createElement('div');
  titleEl.style.cssText = `
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    font-size: 16px;
    font-weight: 600;
    color: #111116;
    flex: 1;
  `;
  titleEl.textContent = title;
  container.appendChild(titleEl);

  if (headerAction) {
    const actionEl = document.createElement('div');
    actionEl.style.cssText = `
      display: flex;
      align-items: center;
      flex-shrink: 0;
    `;
    actionEl.innerHTML = renderToStaticMarkup(<>{headerAction}</>);
    container.appendChild(actionEl);
  }

  return container;
};

const createPopoverContent = (content: string | React.ReactNode, hasHeader: boolean): string => {
  const bodyContent = typeof content === 'string' ? content : renderToStaticMarkup(<>{content}</>);

  const dividerHtml = hasHeader
    ? `<div style="
        height: 1px;
        background-color: #e5e5e5;
        margin: 0 -12px 12px -12px;
      "></div>`
    : '';

  return `
    <style>
      .gm-style-iw-tc { display: none !important; }
      .gm-style-iw-d { overflow: visible !important; padding: 12px !important; }
      .gm-style-iw-ch { padding-top: 12px !important; padding-bottom: 0 !important; }
    </style>
    <div style="
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      min-width: 240px;
      max-width: 320px;
      font-size: 14px;
      color: #111116;
      line-height: 1.5;
      margin-inline-start: -12px;
      margin-block-start: -16px;
      min-width: calc(100% + 12px);
    ">
      ${dividerHtml}
      ${bodyContent}
    </div>
  `;
};

export const PriceMapDot = ({
  lat,
  lng,
  price,
  backgroundColor = '#21A495',
  textColor = '#FFFFFF',
  zIndex,
  popoverTitle,
  popoverContent,
  popoverHeaderAction,
  onOpen,
  showDollarSign = true,
}: PriceMapDotProps) => {
  const { gmap } = useContext<{ gmap: google.maps.Map }>(ZillowMapContext);
  const markerRef = useRef<google.maps.Marker | null>(null);
  const infoWindowRef = useRef<google.maps.InfoWindow | null>(null);

  useEffect(() => {
    if (!gmap || !window.google?.maps?.Marker) return;

    const point = new google.maps.LatLng(lat, lng);

    if (markerRef.current) {
      markerRef.current.setMap(null);
    }

    if (infoWindowRef.current) {
      infoWindowRef.current.close();
    }

    const marker = new google.maps.Marker({
      position: point,
      ...(zIndex != null && { zIndex }),
      icon: {
        url: createPriceLabel(price, backgroundColor, textColor, showDollarSign),
        scaledSize: new google.maps.Size(38, 50),
        anchor: new google.maps.Point(19, 50),
      },
    });

    marker.setMap(gmap);
    markerRef.current = marker;

    if (popoverContent) {
      const contentString = createPopoverContent(popoverContent, !!popoverTitle);

      const infoWindowOptions: google.maps.InfoWindowOptions = {
        content: contentString,
      };

      if (popoverTitle) {
        infoWindowOptions.headerContent = createPopoverHeaderElement(popoverTitle, popoverHeaderAction);
      }

      const infoWindow = new google.maps.InfoWindow(infoWindowOptions);

      infoWindowRef.current = infoWindow;

      marker.addListener('click', () => {
        onOpen?.();

        setTimeout(() => {
          infoWindow.open({
            anchor: marker,
            map: gmap,
          });
        }, 0);
      });

      function handleDocumentClick(event: MouseEvent) {
        const infoWindowContainer = document.querySelector('.gm-style-iw');
        if (infoWindowContainer && infoWindowContainer.contains(event.target as Node)) {
          return;
        }
        infoWindow.close();
      }

      document.addEventListener('click', handleDocumentClick);

      return () => {
        if (markerRef.current) {
          markerRef.current.setMap(null);
        }
        if (infoWindowRef.current) {
          infoWindowRef.current.close();
        }
        document.removeEventListener('click', handleDocumentClick);
      };
    }

    return () => {
      if (markerRef.current) {
        markerRef.current.setMap(null);
      }
      if (infoWindowRef.current) {
        infoWindowRef.current.close();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gmap, lat, lng, price, backgroundColor, textColor, zIndex, popoverTitle]);

  useEffect(() => {
    if (infoWindowRef.current && popoverContent) {
      const contentString = createPopoverContent(popoverContent, !!popoverTitle);
      infoWindowRef.current.setContent(contentString);
    }
  }, [popoverContent, popoverTitle]);

  return null;
};
